import { Link } from 'react-router-dom'
import { ReactElement, ReactNode, useState } from 'react'
import { Tooltip } from '../Tooltip'

/**
 * Component to render a table cell with content that shows a tooltip on overflow and optional actions on the right
 * @param content - String or ReactNode content to display in the cell
 * @param tooltipContent - Optional string to show in tooltip (if not provided, uses content if it's a string)
 * @param badge - Optional ReactNode to display after content (e.g., badges, icons) - hidden when no space
 * @param actions - ReactNode with action buttons/icons to show on the right (typically on hover)
 * @param onClick - Optional click handler for the cell wrapper
 * @param href - Optional href for Link component (if provided, renders as Link instead of div)
 * @param showActionsOnHover - Whether to show actions only on hover (default: true)
 * @param className - Optional additional CSS classes for the wrapper
 * @returns ReactElement
 */
export function ActionsCell({
  content,
  tooltipContent: tooltipContentProp,
  badge,
  actions,
  href,
  showActionsOnHover = true,
  className = ``,
  onClick,
}: {
  content: string | ReactNode
  tooltipContent?: string
  badge?: ReactNode
  actions?: ReactNode
  href?: string
  showActionsOnHover?: boolean
  className?: string
  onClick?: () => void
}): ReactElement {
  const [tooltipState, setTooltipState] = useState<string>()

  const wrapperClasses = `group flex w-full items-center justify-between gap-2 overflow-hidden px-2 text-left ${
    onClick || href ? `h-12 cursor-pointer` : ``
  } ${className}`

  const tooltipText = tooltipContentProp ?? (typeof content === `string` ? content : undefined)
  const contentElement = (
    <>
      <div className="flex min-w-0 items-center gap-2">
        <Tooltip
          className={`min-w-0 flex-1 overflow-hidden ${onClick || href ? `!cursor-pointer` : ``}`}
          placement="bottom"
          message={tooltipState}
        >
          <span
            className={`block ${typeof content === `string` ? `truncate` : ``} text-ui-xs`}
            onMouseOver={(event) => {
              if (
                tooltipText &&
                event.currentTarget instanceof HTMLElement &&
                event.currentTarget.scrollWidth > event.currentTarget.clientWidth
              ) {
                setTooltipState(tooltipText)
              } else {
                setTooltipState(undefined)
              }
            }}
          >
            {content}
          </span>
        </Tooltip>
        {badge && tooltipState == null && (
          <div className="min-w-0 flex-shrink overflow-hidden whitespace-nowrap">{badge}</div>
        )}
      </div>
      {actions && (
        <div className={showActionsOnHover ? `invisible shrink-0 group-hover:visible` : `shrink-0`}>{actions}</div>
      )}
    </>
  )

  if (href) {
    return (
      <Link to={href} className={wrapperClasses}>
        {contentElement}
      </Link>
    )
  }

  if (onClick) {
    return (
      <button onClick={onClick} className={wrapperClasses} type="button">
        {contentElement}
      </button>
    )
  }

  return <div className={wrapperClasses}>{contentElement}</div>
}
